import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaPrint,
} from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const EMIPaymentReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const [showFilters, setShowFilters] = useState(true);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // Dropdown
  const [assetSubTypes, setAssetSubTypes] = useState([]);

  // Search form state
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    customerId: "",
    assetSubType: "",
    doB: "",
    paymentStatus: "",
  });

  // Table data
  const [emiCustomerData, setEmiCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);

  const paymentStatuses = useMemo(
    () => ["Paid", "Pending", "Overdue", "Processing", "Completed"],
    []
  );

  useEffect(() => {
    loadAssetSubTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const loadAssetSubTypes = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/emiListing/assetsubtypes`, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setAssetSubTypes(res.data.data || []);
      } else {
        toast.error(res.data?.message || "Failed to load asset sub types");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load asset sub types");
    }
  };

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const hasSearchCriteria = () => {
    return Object.values(searchData).some((v) => String(v || "").trim() !== "");
  };

  const handleSearch = async () => {
    if (!hasSearchCriteria()) {
      toast.warning("Please enter at least one search criteria!");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/emiListing/search`, {
        params: searchData,
        headers: commonHeaders,
      });

      console.log("res:", res.data);

      if (res.data?.success) {
        setEmiCustomerData(Array.isArray(res.data.data) ? res.data.data : []);
        toast.success(`Found ${res.data.count || (res.data.data || []).length || 0} record(s)`);
      } else {
        setEmiCustomerData([]);
        toast.error(res.data?.message || "Search failed");
      }
    } catch (e) {
      setEmiCustomerData([]);
      toast.error(e?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Whole print
  const handleAllPrint = () => {
    if (!hasSearchCriteria()) {
      toast.warning("Please enter at least one search criteria before printing!");
      return;
    }

    const qs = new URLSearchParams({
      ...searchData,
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    }).toString();

    window.open(`/Sales/EMIPaymentReport_AllPrint?${qs}`, "_blank");
  };

  // Single print
  const handlePrintRow = (row) => {
    const enquiryNo = row?.enquiryNumber;

    if (!enquiryNo) {
      toast.warning("Enquiry Number missing for this row!");
      return;
    }

    const qs = new URLSearchParams({
      enquiryNo: String(enquiryNo),
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    }).toString();

    window.open(`/Sales/EMIPaymentReport_Print?${qs}`, "_blank");
  };

  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const getPaymentStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "paid":
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getDueRemainingColor = (remaining) => {
    const r = Number(remaining || 0);
    if (r <= 5) return "bg-red-100 text-red-800";
    if (r <= 15) return "bg-yellow-100 text-yellow-800";
    return "bg-green-100 text-green-800";
  };

  const totals = useMemo(() => {
    const totalAssetValue = emiCustomerData.reduce(
      (sum, x) => sum + Number(x.assetValue || 0),
      0
    );

    const monthlyEmiTotal = emiCustomerData.reduce(
      (sum, x) => sum + Number(x.emiAmountMonth || 0),
      0
    );

    const pending = emiCustomerData.filter(
      (x) =>
        String(x.currentMonthPaymentStatus || x.paymentStatus || "")
          .toLowerCase()
          .trim() === "pending"
    ).length;

    const overdue = emiCustomerData.filter(
      (x) =>
        String(x.currentMonthPaymentStatus || x.paymentStatus || "")
          .toLowerCase()
          .trim() === "overdue"
    ).length;

    return { totalAssetValue, monthlyEmiTotal, pending, overdue };
  }, [emiCustomerData]);

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMI PAYMENT CUSTOMER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              EMI Payment Report
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Search Criteria
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" /> Enquiry Number
                  </label>
                  <input
                    type="text"
                    value={searchData.enquiryNumber}
                    onChange={(e) => handleInputChange("enquiryNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Enter enquiry number"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaUser className="inline mr-2" /> Customer Name
                  </label>
                  <input
                    type="text"
                    value={searchData.customerName}
                    onChange={(e) => handleInputChange("customerName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Enter customer name"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" /> Customer ID
                  </label>
                  <input
                    type="text"
                    value={searchData.customerId}
                    onChange={(e) => handleInputChange("customerId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Enter customer ID"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Sub Type
                  </label>
                  <select
                    value={searchData.assetSubType}
                    onChange={(e) => handleInputChange("assetSubType", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {assetSubTypes.map((x) => (
                      <option key={x.fld_assetsubtypeid} value={x.fld_assetsubtypeid}>
                        {x.fld_assetsubtypename}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaCalendarAlt className="inline mr-2" /> DoB
                  </label>
                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Payment Status
                  </label>
                  <select
                    value={searchData.paymentStatus}
                    onChange={(e) => handleInputChange("paymentStatus", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {paymentStatuses.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center gap-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleAllPrint}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Sl No</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Lead ID</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Enquiry No</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Customer ID</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Customer Name</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Plot No</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Asset Code</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Asset Sub Type</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Asset Type / Model</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Asset Value</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Interest %</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Overdue %</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Tenure</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">EMI / Month</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Active Date</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Remaining</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Print</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {emiCustomerData.map((x, idx) => (
                  <tr
                    key={idx}
                    className={`${
                      idx % 2 === 0
                        ? isDark
                          ? "bg-gray-800"
                          : "bg-white"
                        : isDark
                        ? "bg-gray-750"
                        : "bg-gray-50"
                    } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"} transition-colors`}
                  >
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.slNo}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.leadId}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.enquiryNumber}</td>
                    <td className={`px-3 py-3 text-xs font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{x.customerId}</td>
                    <td className={`px-3 py-3 text-xs font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{x.customerName}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.plotNumber}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.assetCode}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.assetSubTypeName}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.assetTypeModel}</td>
                    <td className={`px-3 py-3 text-xs font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>{formatAmount(x.assetValue)}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{Number(x.interestRate || 0)}%</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{Number(x.overdueInterest || 0)}%</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.tenurePeriod}</td>
                    <td className={`px-3 py-3 text-xs font-semibold ${isDark ? "text-blue-400" : "text-blue-600"}`}>{formatAmount(x.emiAmountMonth)}</td>
                    <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-900"}`}>{x.emiActivateDate}</td>
                    <td className="px-3 py-3 text-xs">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDueRemainingColor(
                          x.dueRemaining
                        )}`}
                      >
                        {x.dueRemaining}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(
                          x.currentMonthPaymentStatus || x.paymentStatus
                        )}`}
                      >
                        {x.currentMonthPaymentStatus || x.paymentStatus}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <button
                        onClick={() => handlePrintRow(x)}
                        className="p-2 rounded hover:bg-black/5"
                        title="Print"
                      >
                        <FaPrint />
                      </button>
                    </td>
                  </tr>
                ))}

                {!loading && emiCustomerData.length === 0 && (
                  <tr>
                    <td
                      colSpan={18}
                      className={`px-3 py-6 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                    >
                      No data to display. Search to load results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`mt-6 p-4 rounded-lg border ${
              isDark ? "bg-gray-800/30 border-gray-600" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Search Results
                </h4>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Showing {emiCustomerData.length} EMI payment customers
                </p>
              </div>

              <div className="grid grid-cols-4 gap-4 text-center">
                <div className={`p-3 rounded-lg ${isDark ? "bg-green-900/30" : "bg-green-50"}`}>
                  <div className="text-lg font-bold text-green-600">
                    {formatAmount(totals.totalAssetValue)}
                  </div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Total Asset Value
                  </div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-blue-900/30" : "bg-blue-50"}`}>
                  <div className="text-lg font-bold text-blue-600">
                    {formatAmount(totals.monthlyEmiTotal)}
                  </div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Monthly EMI Total
                  </div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-yellow-900/30" : "bg-yellow-50"}`}>
                  <div className="text-lg font-bold text-yellow-600">{totals.pending}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Pending
                  </div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-red-900/30" : "bg-red-50"}`}>
                  <div className="text-lg font-bold text-red-600">{totals.overdue}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Overdue
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default EMIPaymentReport;